import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, Loader2 } from "lucide-react";

/**
 * A link badge with hover preview for the About Me section. Borrowed from https://www.gulipad.com/, kudos to him! <3
 */
const LinkPreviewBadge = ({
    link,
    display,
    openInNewTab = true,
}) => {
    const [showPreview, setShowPreview] = useState(false);
    const [coords, setCoords] = useState({ x: 0, y: 0, width: 0 });
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [hoverStart, setHoverStart] = useState(null);
    const [previewPosition, setPreviewPosition] = useState("right");

    // Set up load error detection
    useEffect(() => {
        let timer = null;
        if (showPreview && !isLoaded && !loadError) {
            timer = setTimeout(() => {
                if (!isLoaded) {
                    setLoadError(true);
                }
            }, 3000);
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [showPreview, isLoaded, loadError]);

    // Handle scroll/gesture events
    useEffect(() => {
        function handleScrollOrGesture() {
            hidePreviewImmediately();
        }

        if (showPreview) {
            window.addEventListener("scroll", handleScrollOrGesture, {
                passive: true,
            });
            window.addEventListener("wheel", handleScrollOrGesture, {
                passive: true,
            });
            window.addEventListener("touchmove", handleScrollOrGesture, {
                passive: true,
            });
        }

        return () => {
            window.removeEventListener("scroll", handleScrollOrGesture);
            window.removeEventListener("wheel", handleScrollOrGesture);
            window.removeEventListener("touchmove", handleScrollOrGesture);
        };
    }, [showPreview]);

    function handleMouseEnter(e) {
        setHoverStart(Date.now());
        setShowPreview(true);

        const rect = e.currentTarget.getBoundingClientRect();

        // Determine if we're on the right side of the screen
        const isRightSide = rect.left > window.innerWidth / 2;

        // Set the position based on where we are on the screen
        setPreviewPosition(isRightSide ? "left" : "right");

        setCoords({
            x: rect.left,
            y: rect.bottom + window.scrollY,
            width: rect.width,
        });
    }

    function handleMouseLeave() {
        hidePreviewImmediately();
    }

    function hidePreviewImmediately() {
        setShowPreview(false);
        resetPreviewState();
    }

    function resetPreviewState() {
        setIsLoaded(false);
        setLoadError(false);
        setHoverStart(null);
    }

    function handlePreviewClick() {
        if (openInNewTab) {
            window.open(link, "_blank", "noopener,noreferrer");
        } else {
            window.location.href = link;
        }
    }

    // Extract domain from the link
    const getDomain = (url) => {
        try {
            return new URL(url).hostname;
        } catch (e) {
            return "This site";
        }
    };

    return (
        <span
            className="inline-block"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* The Badge - styled for portfolio (slate/dark theme) */}
            <a
                href={link}
                target={openInNewTab ? "_blank" : "_self"}
                rel={openInNewTab ? "noopener noreferrer" : undefined}
                className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 hover:text-slate-900 transition-all duration-200 border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow"
            >
                {display}
                {openInNewTab && <ExternalLink className="ml-1.5 h-3.5 w-3.5 opacity-60" />}
            </a>

            {/* The Preview rendered via a portal */}
            {showPreview &&
                createPortal(
                    <div
                        className="absolute"
                        style={{
                            top: coords.y + 8,
                            left: previewPosition === "right" ? coords.x : "auto",
                            right:
                                previewPosition === "left"
                                    ? `calc(100vw - ${coords.x + coords.width}px)`
                                    : "auto",
                            position: "absolute",
                            zIndex: 99999,
                            cursor: "pointer",
                        }}
                        onClick={handlePreviewClick}
                    >
                        {!loadError ? (
                            <div className="w-96 h-64 bg-white shadow-xl border border-slate-200 rounded-xl overflow-hidden">
                                {!isLoaded && (
                                    <div className="w-full h-full flex items-center justify-center bg-slate-50">
                                        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                                    </div>
                                )}
                                <div
                                    style={{
                                        width: "1200px",
                                        height: "900px",
                                        transform: "scale(0.4)",
                                        transformOrigin: "top left",
                                        display: isLoaded ? "block" : "none",
                                    }}
                                >
                                    <iframe
                                        src={link}
                                        title="Link preview"
                                        style={{
                                            width: "1200px",
                                            height: "900px",
                                            border: "none",
                                            pointerEvents: "none",
                                        }}
                                        onLoad={(e) => {
                                            if (hoverStart && Date.now() - hoverStart < 200) {
                                                setLoadError(true);
                                                return;
                                            }
                                            try {
                                                const iframe = e.currentTarget;
                                                const doc =
                                                    iframe.contentDocument ||
                                                    iframe.contentWindow?.document;
                                                if (
                                                    doc &&
                                                    doc.body.innerText.toLowerCase().includes("refused")
                                                ) {
                                                    setLoadError(true);
                                                } else {
                                                    setIsLoaded(true);
                                                }
                                            } catch (err) {
                                                setIsLoaded(true);
                                            }
                                        }}
                                        onError={() => setLoadError(true)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="w-80 bg-white text-slate-800 shadow-xl border border-slate-200 rounded-xl p-4">
                                <p className="text-sm font-medium">Preview Unavailable</p>
                                <p className="text-xs text-slate-500 mt-1">
                                    Hit the link to view it in its original context.
                                </p>
                            </div>
                        )}
                    </div>,
                    document.body
                )}
        </span>
    );
};

export default LinkPreviewBadge;